import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { isAppBilling, hasActiveSubscription, getProducts } from "../lib/playBilling";

const PremiumContext = createContext(null);

const LEGACY_TRIAL_KEY = "fw_trial_start";
const LEGACY_GRACE_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

// Read (never create) the legacy local-trial marker. Users who installed under
// the old 30-day local-trial model are grandfathered for the remainder of that
// window and skip the onboarding paywall. Brand-new installs have no key and go
// straight to the freemium model.
function readLegacyStart() {
  try {
    const s = localStorage.getItem(LEGACY_TRIAL_KEY);
    return s ? parseInt(s, 10) : null;
  } catch {
    return null;
  }
}

function isForceFree() {
  try {
    return localStorage.getItem("force_free") === "1";
  } catch {
    return false;
  }
}

export function PremiumProvider({ children }) {
  const inApp = isAppBilling();
  const [subscribed, setSubscribed] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(inApp);

  const forceFree = isForceFree();
  const legacyStart = readLegacyStart();
  const isLegacy = legacyStart != null;
  const legacyGraceActive = isLegacy && Date.now() < legacyStart + LEGACY_GRACE_DAYS * DAY_MS;

  const refresh = useCallback(async () => {
    if (!inApp) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const [sub, prods] = await Promise.all([hasActiveSubscription(), getProducts()]);
    setSubscribed(sub);
    setProducts(prods);
    setLoading(false);
  }, [inApp]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Web (public site) = always free. Inside the Android TWA = freemium: premium
  // requires an active Google Play subscription (a Play-managed free trial counts
  // as active) OR a still-valid legacy grace window. `force_free` forces the true
  // non-premium experience for QA on any device.
  const isPremium = inApp ? (!forceFree && (subscribed || legacyGraceActive)) : true;

  return (
    <PremiumContext.Provider
      value={{ isPremium, inApp, subscribed, products, loading, refresh, isLegacy, legacyGraceActive, forceFree }}
    >
      {children}
    </PremiumContext.Provider>
  );
}

export const usePremium = () => useContext(PremiumContext);
